import { useEffect, useMemo, useState } from "react";
import { api, formatErr, inr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { handleSimpleFormNav } from "@/lib/keyboardNav";
import { Plus, Pencil, Trash2, Receipt, ArrowRightLeft, BookText, MinusCircle, PlusCircle, Send } from "lucide-react";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";
import { PaginationBar, usePagination } from "@/components/Pagination";

function today() { return new Date().toISOString().slice(0, 10); }

const V_META = {
  receipt:      { label: "Receipt Voucher",  party: "customer", icon: Send,          tone: "text-green-500",   note: "Money received from customers or other sources." },
  payment:      { label: "Payment Voucher",  party: "vendor",   icon: Receipt,       tone: "text-destructive", note: "Money paid to vendors, staff, or for expenses." },
  journal:      { label: "Journal Voucher",  party: "other",    icon: BookText,      tone: "text-primary",     note: "Internal accounting adjustments & entries." },
  contra:       { label: "Contra Voucher",   party: "other",    icon: ArrowRightLeft,tone: "text-primary",     note: "Cash to bank or bank to cash transfers." },
  debit_note:   { label: "Debit Note",       party: "vendor",   icon: MinusCircle,   tone: "text-destructive", note: "Issued against purchase returns or adjustments." },
  credit_note:  { label: "Credit Note",      party: "customer", icon: PlusCircle,    tone: "text-green-500",   note: "Issued against sale returns or adjustments." },
};
const MODES = ["cash", "bank", "upi", "cheque", "other"];
const empty = { date: today(), party_type: "", party_id: "", party_name: "", amount: 0, payment_mode: "cash", bank_account_id: "", reference_number: "", narration: "", debit_account: "", credit_account: "" };

export default function Vouchers() {
  const [params, setParams] = useSearchParams();
  const vtype = params.get("type") || "receipt";
  const meta = V_META[vtype];

  const [list, setList] = useState([]);
  const pg = usePagination(list);
  const [customers, setCustomers] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [banks, setBanks] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [f, setF] = useState(empty);

  const load = () => api.get(`/vouchers?voucher_type=${vtype}`).then(r => setList(r.data)).catch(e => toast.error(formatErr(e)));
  useEffect(() => {
    load();
    api.get("/customers").then(r => setCustomers(r.data)).catch(() => {});
    api.get("/vendors").then(r => setVendors(r.data)).catch(() => {});
    api.get("/bank-accounts").then(r => setBanks(r.data)).catch(() => {});
    setF({ ...empty, party_type: V_META[vtype].party });
  }, [vtype]); // eslint-disable-line

  const parties = useMemo(() => meta.party === "vendor" ? vendors : meta.party === "customer" ? customers : [], [meta.party, vendors, customers]);

  const save = async () => {
    try {
      const payload = { ...f, voucher_type: vtype,
        amount: Number(f.amount),
        party_id: f.party_id || null,
        party_name: f.party_name || null,
        party_type: f.party_type || null,
        bank_account_id: f.payment_mode === "bank" ? (f.bank_account_id || null) : null,
        reference_number: f.reference_number || null,
        narration: f.narration || null,
        debit_account: f.debit_account || null,
        credit_account: f.credit_account || null,
      };
      if (editId) await api.patch(`/vouchers/${editId}`, payload);
      else await api.post("/vouchers", payload);
      toast.success(editId ? "Updated" : "Voucher created");
      setF({ ...empty, party_type: meta.party }); setEditId(null); setOpen(false); load();
    } catch (e) { toast.error(formatErr(e)); }
  };
  const edit = (v) => { setF({ ...empty, ...v, bank_account_id: v.bank_account_id || "" }); setEditId(v.id); setOpen(true); };
  const del = async (id) => {
    if (!confirm("Delete this voucher?")) return;
    try { await api.delete(`/vouchers/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const Ico = meta.icon;
  const isJournalOrContra = vtype === "journal" || vtype === "contra";

  return (
    <div className="p-8">
      <div className="mb-6">
        <div className="text-primary text-xs uppercase tracking-[0.3em] mb-2 flex items-center gap-2"><Ico className={`w-3 h-3 ${meta.tone}`}/> Accounts · Vouchers</div>
        <h1 className="font-display font-black text-4xl uppercase tracking-tighter">{meta.label}</h1>
        <div className="text-xs text-muted-foreground mt-2">{meta.note}</div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 mb-6">
        {Object.entries(V_META).map(([k, m]) => {
          const Ic = m.icon;
          return (
            <button
              key={k} data-testid={`vtab-${k}`}
              onClick={() => setParams({ type: k })}
              className={`border rounded-sm p-3 text-left transition-colors ${vtype === k ? "border-primary bg-primary/10 text-primary" : "border-border bg-card hover:border-primary/40"}`}
            >
              <Ic className={`w-4 h-4 mb-1 ${vtype === k ? "text-primary" : m.tone}`}/>
              <div className="text-xs font-semibold">{m.label}</div>
            </button>
          );
        })}
      </div>

      <div className="flex items-center justify-between mb-4">
        <div className="text-sm text-muted-foreground">{list.length} record(s)</div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setF({ ...empty, party_type: meta.party }); setEditId(null); } }}>
          <DialogTrigger asChild>
            <Button data-testid="new-voucher-btn" className="rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em] text-xs">
              <Plus className="w-4 h-4 mr-1"/> New {meta.label}
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-sm bg-card border-border max-w-2xl max-h-[90vh] overflow-y-auto" onKeyDownCapture={handleSimpleFormNav}>
            <DialogHeader><DialogTitle className="font-display uppercase">{editId ? "Edit" : "New"} {meta.label}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-3">
                <div><Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Date</Label><Input data-testid="v-date" type="date" value={f.date} onChange={(e) => setF(x => ({ ...x, date: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border"/></div>
                <div><Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Amount ₹</Label><Input data-testid="v-amount" type="number" value={f.amount} onChange={(e) => setF(x => ({ ...x, amount: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border text-right"/></div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Payment Mode</Label>
                  <Select value={f.payment_mode} onValueChange={(v) => setF(x => ({ ...x, payment_mode: v }))}>
                    <SelectTrigger data-testid="v-mode" className="mt-1 rounded-sm bg-secondary border-border"><SelectValue/></SelectTrigger>
                    <SelectContent>{MODES.map(m => <SelectItem key={m} value={m}>{m.toUpperCase()}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>

              {f.payment_mode === "bank" && (
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Bank Account</Label>
                  <Select value={f.bank_account_id} onValueChange={(v) => setF(x => ({ ...x, bank_account_id: v }))}>
                    <SelectTrigger data-testid="v-bank" className="mt-1 rounded-sm bg-secondary border-border"><SelectValue placeholder="Select bank"/></SelectTrigger>
                    <SelectContent>{banks.map(b => <SelectItem key={b.id} value={b.id}>{b.name} · {b.bank_name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}

              {!isJournalOrContra && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">{meta.party === "vendor" ? "Vendor" : "Customer"}</Label>
                    <Select value={f.party_id} onValueChange={(v) => {
                      const p = parties.find(x => x.id === v);
                      setF(x => ({ ...x, party_id: v, party_name: p?.name || "" }));
                    }}>
                      <SelectTrigger data-testid="v-party" className="mt-1 rounded-sm bg-secondary border-border"><SelectValue placeholder={`Select ${meta.party}`}/></SelectTrigger>
                      <SelectContent>{parties.map(p => <SelectItem key={p.id} value={p.id}>{p.name} · {p.phone}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Or Free-text Name</Label><Input data-testid="v-party-name" value={f.party_name} onChange={(e) => setF(x => ({ ...x, party_id: "", party_name: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border"/></div>
                </div>
              )}

              {isJournalOrContra && (
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Debit Account</Label><Input data-testid="v-debit" value={f.debit_account} onChange={(e) => setF(x => ({ ...x, debit_account: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border" placeholder={vtype === "contra" ? "e.g. Bank / HDFC" : "e.g. Rent Expense"}/></div>
                  <div><Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Credit Account</Label><Input data-testid="v-credit" value={f.credit_account} onChange={(e) => setF(x => ({ ...x, credit_account: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border" placeholder={vtype === "contra" ? "e.g. Cash" : "e.g. Cash / Bank"}/></div>
                </div>
              )}

              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Reference / Invoice #</Label>
                <Input data-testid="v-ref" value={f.reference_number} onChange={(e) => setF(x => ({ ...x, reference_number: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border" placeholder="Optional"/>
              </div>
              <div><Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Narration</Label><Textarea data-testid="v-narration" value={f.narration} onChange={(e) => setF(x => ({ ...x, narration: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border" rows={2}/></div>

              <Button data-testid="save-voucher-btn" onClick={save} disabled={!f.date || !f.amount} className="w-full rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em]">
                {editId ? "Update" : "Save Voucher"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="border border-border bg-card rounded-sm overflow-hidden overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            <tr>
              <th className="text-left p-3">Voucher #</th><th className="text-left p-3">Date</th>
              <th className="text-left p-3">Party / Accounts</th><th className="text-left p-3">Mode</th>
              <th className="text-right p-3">Amount</th><th className="text-left p-3">Narration</th>
              <th className="text-right p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {list.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-muted-foreground">No {meta.label.toLowerCase()} yet</td></tr>}
            {pg.pageItems.map(v => (
              <tr key={v.id} data-testid={`v-row-${v.id}`} className="border-t border-border hover:bg-secondary/50">
                <td className="p-3 font-mono text-xs text-primary uppercase">{v.voucher_number}</td>
                <td className="p-3 font-mono text-xs">{v.date}</td>
                <td className="p-3 text-xs">
                  {(vtype === "journal" || vtype === "contra")
                    ? <>Dr <span className="text-primary">{v.debit_account}</span> · Cr <span className="text-primary">{v.credit_account}</span></>
                    : (v.party_name || "-")}
                </td>
                <td className="p-3 text-xs uppercase">{v.payment_mode}</td>
                <td className={`p-3 text-right font-mono ${meta.tone}`}>{inr(v.amount)}</td>
                <td className="p-3 text-xs text-muted-foreground italic">{v.narration || "-"}</td>
                <td className="p-3 text-right">
                  <Button data-testid={`edit-v-${v.id}`} size="sm" variant="ghost" onClick={() => edit(v)} className="h-7 w-7 p-0"><Pencil className="w-3 h-3"/></Button>
                  <Button data-testid={`del-v-${v.id}`} size="sm" variant="ghost" onClick={() => del(v.id)} className="h-7 w-7 p-0 text-destructive"><Trash2 className="w-3 h-3"/></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <PaginationBar pg={pg}/>
      </div>
    </div>
  );
}
